import React, {useEffect, useState} from "react";
import {billPay, getBills, updateBillStatus} from "../../Api/Api";
import BillDetailsModal from "./BillDetialsModal";
import {useWorkshop} from "../Common/WorkshopContext";
import {FaCheck, FaEye, FaPlus, FaSearch} from "react-icons/fa";
import ConfirmationModal from "./ConfirmationModal";
import Pagination from "../Pagination/Pagination";
import {useNotification} from    "../Notification/NotificationContext";
import PaymentModal from "./PaymentModal";
import {Link} from "react-router-dom";

const BillList = () => {
    const [bills, setBills] = useState([]);
    const [selectedBill, setSelectedBill] = useState(null);
    const [showBillDetails, setShowBillDetails] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const { workshopId } = useWorkshop();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const { showNotification } = useNotification();
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10; // Adjust as needed

    useEffect(() => {
        fetchBills(currentPage);
    }, [currentPage,searchTerm, statusFilter]);

    const fetchBills = () => {
        setLoading(true);
        getBills(workshopId, currentPage, pageSize,searchTerm, statusFilter)
            .then((response) => {
                setBills(response.data.content); // Extract paginated content
                setTotalPages(response.data.totalPages);
                setTotalCount(response.data.totalElements);
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false)
                showNotification(error.message, "error")
            });
    };

    const handleBillClick = (bill) => {
        setSelectedBill(bill);
        setShowBillDetails(true);
    };

    const closeModal = () => {
        setSelectedBill(null);
        setShowBillDetails(false);
    };

    const handleMarkAsPaid = (bill) => {
        setSelectedBill(bill);
        setShowConfirmation(true);
    };

    const handlePaymentModal = (bill) => {
        setSelectedBill(bill);
        setShowPaymentModal(true);
    };

    const confirmPayment = (amountPaid) => {
        // Call backend with billId + amountPaid
        billPay(selectedBill.id, amountPaid)
            .then(() => {
                setShowPaymentModal(false)
                fetchBills()
            })
            .catch((error) => {
                showNotification(error.message, "error");
            });
    };


    const confirmMarkAsPaid = () => {
        updateBillStatus(selectedBill.id, "PAID").then(() => {
            setBills((prevBills) =>
                prevBills.map((bill) =>
                    bill.id === selectedBill.id ? { ...bill, status: "PAID" } : bill
                )
            );
            setShowConfirmation(false);
            setSelectedBill(null);
        }).catch((error) => {
            showNotification(error.message, "error")
        });
    };

    return (
        <div className="mt-6">
            <div className="max-w-screen-xl mx-auto">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="bg-teal-400 text-white p-4 rounded-t-lg flex flex-col sm:flex-row justify-between gap-4 items-center">
                        <h2 className="text-xl font-semibold">Bill List</h2>
                        <div className="flex gap-2 items-center w-full sm:w-auto">
                            <div className="relative w-full sm:w-64">
                                <input
                                    type="text"
                                    placeholder="Search by name "
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 rounded-md focus:outline-none text-gray-700"
                                />
                                <FaSearch className="absolute left-3 top-2.5 text-gray-400" />
                            </div>
                            <select
                                className="border px-4 py-2 rounded-md text-gray-700 bg-white focus:outline-none"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="ALL">All</option>
                                <option value="PENDING">Pending</option>
                                <option value="PAID">Paid</option>
                            </select>
                        </div>
                    </div>

                    {/* Main Content */}
                    {bills && bills.length > 0 ? (
                    <div className="p-6">
                        <div className="overflow-x-auto shadow-md rounded-lg">
                            <table className="min-w-full bg-white">
                                <thead className="bg-gray-50 text-gray-800">
                                <tr>
                                    <th className="p-2 text-center">Bill ID</th>
                                     <th className="p-2 text-left">Customer Name</th>
                                    <th className="p-2 text-left">Date</th>
                                    <th className="p-2 text-left">Amount</th>
                                    <th className="p-2 text-left">Discount</th>
                                    <th className="p-2 text-left">Paid</th>
                                    <th className="p-2 text-left">Status</th>
                                    <th className="p-2 text-left">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {bills.map((bill) => (
                                    <tr key={bill.id} className="border-b hover:bg-gray-100">
                                        <td className="p-1 text-center">{bill.id}</td>
                                        <td className="p-2 hover:underline">
                                            <Link to={`/customers/${bill?.customer?.id}/workshops/${workshopId}/profile`}> {bill?.customer?.name}</Link>

                                        </td>
                                        <td className="p-1">{new Date(bill.date).toLocaleDateString()}</td>
                                        <td className="p-1">&#x20B9; {bill.totalAmount?.toFixed(2)}</td>
                                        <td className="p-1">{bill.discount > 0 ? bill.discount: 0} %</td>
                                        <td className="p-1">&#x20B9; {bill.amountPaid?.toFixed(2)}</td>
                                        <td className="p-1">
                                                <span
                                                    className={`px-3 py-1 rounded-full text-sm ${
                                                        bill.status === "PAID"
                                                            ? "bg-green-200 text-green-800"
                                                            : "bg-yellow-200 text-yellow-800"
                                                    }`}
                                                >
                                                    {bill.status}
                                                </span>
                                        </td>
                                        <td className="p-1">
                                            <div className="flex gap-2">
                                                <button
                                                    className="bg-teal-400 text-white px-4 py-2 rounded-md flex items-center hover:bg-teal-500"
                                                    onClick={() => handleBillClick(bill)}
                                                >
                                                    <FaEye />
                                                    <span className="ml-2">View</span>
                                                </button>
                                                {/*{bill.status !== "PAID" && (*/}
                                                {/*    <button*/}
                                                {/*        className="bg-green-400 text-white px-4 py-2 rounded-md flex items-center hover:bg-green-500"*/}
                                                {/*        onClick={() => handleMarkAsPaid(bill)}*/}
                                                {/*    >*/}
                                                {/*        <FaCheck />*/}
                                                {/*        <span className="ml-2">Mark as Paid</span>*/}
                                                {/*    </button>*/}
                                                {/*)}*/}
                                                {bill.status === "PENDING" && (
                                                    <button
                                                        className="bg-green-400 text-white px-4 py-2 rounded-md flex items-center hover:bg-green-500"
                                                        onClick={() => handlePaymentModal(bill)}
                                                    >
                                                        <FaCheck />
                                                        <span className="ml-2">Pay</span>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination UI */}
                        <div className="flex justify-start mt-2">
                            <Pagination
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                currentPage={currentPage}
                                totalCount={totalCount}
                            />
                        </div>
                    </div>
                    ) : (
                        <div className="text-center text-gray-600 py-10">
                            No Bills found.
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {selectedBill && (
                <BillDetailsModal show={showBillDetails} bill={selectedBill} onClose={closeModal} />
            )}
            {showConfirmation && (
                <ConfirmationModal
                    show={showConfirmation}
                    message={`Are you sure you want to mark Bill ID: ${selectedBill.id} as paid?`}
                    onConfirm={confirmMarkAsPaid}
                    onCancel={() => setShowConfirmation(false)}
                />
            )}
            {showPaymentModal && (
                <PaymentModal
                    show={showPaymentModal}
                    bill={selectedBill}
                    onConfirm={confirmPayment}
                    onCancel={() => setShowPaymentModal(false)}
                />
            )}
        </div>
    );
};

export default BillList;
